import { FilterType } from './urlHelpers';
import { lastSettledAt } from './meterAlerts';

/**
 * Filter keys the meters list supports, and how each is stored in the URL.
 * `meterIds` holds business meter IDs (`meter.meterId`), not database keys — the
 * meters API does not return a database key.
 */
export const METER_FILTER_SCHEMA = {
  health: FilterType.STRING,
  meterIds: FilterType.STRING_ARRAY,
  module: FilterType.STRING,
  category: FilterType.STRING,
  subCategory: FilterType.STRING,
  kpi: FilterType.STRING,
  sourceIds: FilterType.NUMBER_ARRAY,
  subLocationId: FilterType.NUMBER,
};

/**
 * The three states the health strip counts, and the filter it sets.
 *
 * Reporting and problems overlap on purpose: a meter can be sending every
 * minute and sending rubbish, and both are true at once. Down and problems do
 * not — a meter cannot be both silent and connected-but-unusable, and counting
 * it as both is what had two tiles disagreeing with the database.
 */
export const Health = {
  REPORTING: 'reporting',
  PROBLEMS: 'problems',
  DOWN: 'down',
};

/** Reading states that mean connected but unusable. */
const PROBLEM_STATES = ['INVALID', 'NULL_DATA'];

/**
 * Whether a meter is in a given health state.
 *
 * The strip counts meters with this and the grid filters with it, so a tile
 * reading 3 can never open a list of two.
 *
 * A data problem means connected but unusable, so a meter that is down is not
 * one however its last reading was labelled. Two meters on this fleet sat in
 * NULL_DATA having last produced a valid reading a week and three months ago:
 * the API already calls them inactive, and counting them as a data problem as
 * well made a tile read 4 where the database held 2.
 *
 * @param {object} meter - Meter as returned by the meters API.
 * @param {string} health - One of Health.
 * @returns {boolean} True when the meter belongs in that tile.
 */
export const inHealth = (meter, health) => {
  if (health === Health.REPORTING) return Boolean(meter?.isActive);
  if (health === Health.DOWN) return !meter?.isActive;
  if (health === Health.PROBLEMS) {
    return Boolean(meter?.isActive) && PROBLEM_STATES.includes(meter?.readingState);
  }

  return true;
};

/**
 * Finds the KPI a meter is mapped to for a given financial year.
 * @param {object} meter - Meter as returned by the meters API.
 * @param {string} financialYear - Financial year to look up, e.g. '2025-2026'.
 * @returns {object|undefined} The KPI details, or undefined when the meter has no mapping.
 */
export const findKpiForYear = (meter, financialYear) =>
  (meter.financialYearKpis || []).find(
    (mapping) => mapping.financialYear === financialYear
  )?.kpi;

/**
 * Picks the most recent financial year from the list returned by the API.
 * Does not mutate the supplied array.
 * @param {Array<{financial_year_value: string}>} financialYears - Available financial years.
 * @returns {string|undefined} The latest financial year value, or undefined when none exist.
 */
export const latestFinancialYear = (financialYears) =>
  [...(financialYears || [])].sort((a, b) =>
    String(b.financial_year_value).localeCompare(String(a.financial_year_value))
  )[0]?.financial_year_value;

/**
 * Builds the Module › Category › Sub Category › KPI trail for a meter.
 * Empty levels are dropped so a meter without a sub-category still reads cleanly.
 * @param {object} kpi - KPI details as returned by findKpiForYear.
 * @returns {Array<string>} Ordered trail labels, empty when the meter has no KPI.
 */
export const buildKpiTrail = (kpi) =>
  [kpi?.module, kpi?.category, kpi?.subCategory, kpi?.kpi].filter(Boolean);

/**
 * Reads the meter's forward-flow totaliser.
 *
 * This is the meter's lifetime running total since installation or reset — not
 * consumption for any period, and not net of reverse flow. Consumption is the
 * difference between two totaliser readings, which the details page charts.
 *
 * @param {object} meter - Meter as returned by the meters API.
 * @returns {number|null} The forward totaliser, or null when none is available.
 */
export const latestReading = (meter) => {
  const value = Number(meter?.currentMetrics?.forward);
  return Number.isFinite(value) ? value : null;
};

/**
 * Reads the meter's reverse-flow totaliser as a positive magnitude.
 * The device reports it negative; net throughput is forward minus this.
 * @param {object} meter - Meter as returned by the meters API.
 * @returns {number|null} Reverse flow, or null when the meter reports none.
 */
export const reverseReading = (meter) => {
  const raw = meter?.currentMetrics?.backward ?? meter?.currentMetrics?.rawBackward;
  const value = Math.abs(Number(raw));
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Renders an age as the coarsest unit that still reads naturally.
 * @param {string|Date} since - Timestamp to measure from.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {string|null} e.g. '16 seconds', '4 min', '3 h', '28 days'; null if unusable.
 */
export const humaniseAge = (since, now = () => new Date()) => {
  if (!since) return null;

  const seconds = Math.floor((now().getTime() - new Date(since).getTime()) / 1000);
  if (!Number.isFinite(seconds) || seconds < 0) return null;

  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
};

/**
 * Describes how fresh a meter's data is, for display beside the reading.
 * @param {object} meter - Meter as returned by the meters API.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{tone: string, label: string}|null} Tone and wording, or null when never seen.
 */
export const describeFreshness = (meter, now = () => new Date()) => {
  if (!meter?.lastSeenAt) return null;

  const minutes = Math.floor((now().getTime() - new Date(meter.lastSeenAt).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 0) return null;

  if (minutes < 60) {
    return { tone: 'ok', label: `Reporting · ${minutes < 1 ? 'just now' : `${minutes} min ago`}` };
  }
  if (minutes < 1440) {
    return { tone: 'stale', label: `No data for ${Math.floor(minutes / 60)} h` };
  }
  return { tone: 'stale', label: `No data for ${Math.floor(minutes / 1440)} days` };
};

/**
 * How long a meter may stay silent before it counts as inactive, when it does
 * not configure its own. Matches the figure the meters query coalesces to.
 */
const DEFAULT_INACTIVE_MINUTES = 60;

/**
 * When an inactive meter fell silent, or null when it has not.
 *
 * A meter can be inactive without being silent. One sending nulls is still
 * transmitting — its last reading arrived seconds ago — but counts as inactive
 * because nothing usable has come out of it. Dating that from the last reading
 * gave "Inactive for 12 seconds" on a meter mid-transmission, which describes
 * nothing: it did not stop, it is saying nothing.
 *
 * The silence has to outlast the meter's own tolerance before there is an
 * outage to date. Where it has not, the reading state is what explains the
 * card, and the card already carries it.
 *
 * @param {object} meter - Meter as returned by the meters API.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {string|null} When it last reported, once that is a real gap.
 */
const silentSince = (meter, now) => {
  if (!meter?.lastSeenAt) return null;

  const tolerance =
    (meter.inactiveDurationMinutes ?? DEFAULT_INACTIVE_MINUTES) * 60000;
  const silent = now().getTime() - new Date(meter.lastSeenAt).getTime();

  return silent >= tolerance ? meter.lastSeenAt : null;
};

/**
 * Describes how long a meter has been in the state it is in.
 *
 * The two states are dated from different things, because they are different
 * questions. A meter that is down has been down since its last reading, and
 * `lastSeenAt` says exactly when that was.
 *
 * A meter that is up is dated from its alert history — when it last returned
 * to normal — which is the same figure the details page reports and the sense
 * a reader means by "active since". The flow status carries an `activeSince`,
 * and it is not that: the job maintaining it restarts the run whenever a
 * gateway misses a beat, so on this fleet it reported five hours for a meter
 * that had been reporting since April.
 *
 * A meter with no alert history, or with one still open, has nothing to date
 * from. Nothing is claimed there rather than reaching for a number that would
 * be wrong.
 *
 * @param {object} meter - Meter as returned by the meters API.
 * @param {Array<object>} alerts - That meter's alert instances.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{active: boolean, since: string|null, age: string|null,
 *   label: string|null}} The state, when it began, how long that has been, and
 *   how to word it.
 */
export const describeActivity = (meter, alerts = [], now = () => new Date()) => {
  const active = Boolean(meter?.isActive);
  const since = active ? lastSettledAt(alerts) : silentSince(meter, now);
  const age = humaniseAge(since, now);

  if (since && age) {
    return {
      active,
      since,
      age,
      label: active ? `Active for ${age}` : `Inactive for ${age}`,
    };
  }

  return {
    active,
    since: null,
    age: null,
    label: !active && !meter?.lastSeenAt ? 'Never reported' : null,
  };
};

/**
 * Every reading state, worded for a reader.
 *
 * `NULL_DATA` and the rest are the names the device protocol uses. They were
 * printed straight onto the card, the details page and the alert history,
 * where nobody outside the team can be expected to know that NULL_DATA means
 * the meter is transmitting but sending nothing usable.
 */
const READING_STATE_LABELS = {
  VALID: 'Online',
  INVALID: 'Data issue',
  NULL_DATA: 'Meter off',
  NO_DATA: 'Gateway off',
};

/**
 * Names a reading state in plain English.
 *
 * @param {string} state - Reading state as the API returns it.
 * @returns {string} Wording for a reader, or a dash when there is no state.
 */
export const readingStateLabel = (state) =>
  READING_STATE_LABELS[state] || (state ? String(state) : '—');

/**
 * Why a meter that is silent right now has nothing to show.
 *
 * These are the alert email's own three words for the same three faults
 * (`buildNotificationPayload` in meter-status.cron.ts). Whoever gets paged
 * about a meter reads "Meter Off" in the mail and should read "Meter Off" on
 * the chart — a second vocabulary for one fault is one to reconcile.
 *
 * A meter transmitting empty packets and a gateway that has gone off the air
 * look identical on the chart and need different people to fix them, which is
 * the whole reason for naming which it is.
 *
 * @param {string} state - Reading state as the API returns it.
 * @returns {string} The fault, as the alert email words it.
 */
export const gapReasonFor = (state) =>
  state === 'INVALID' || state === 'NULL_DATA'
    ? READING_STATE_LABELS[state]
    : READING_STATE_LABELS.NO_DATA;

/**
 * Reading states in which the meter is transmitting but its numbers are not
 * usable, and how to say so.
 *
 * `NO_DATA` is deliberately absent: the status job sets it when a meter stops
 * reporting, which is the same fact the inactive state already carries, and
 * saying it twice on one card reads as two separate faults.
 */
const UNUSABLE_READING_STATES = {
  INVALID: READING_STATE_LABELS.INVALID,
  NULL_DATA: READING_STATE_LABELS.NULL_DATA,
};

/**
 * Reports whether a meter's readings can be trusted, and for how long they
 * have not been.
 *
 * A dead meter announces itself — the card goes grey and the reading stops
 * moving. A meter transmitting bad data does not: it stays green, its
 * timestamp keeps advancing, and the figure on the card is whatever nonsense
 * it last sent.
 *
 * The age is what makes the badge actionable. Two meters both reading
 * "Readings empty" look identical, and one of them last produced a usable
 * figure three days ago while the other last did in May. It is measured from
 * `lastValidAt` rather than from the last reading, because these meters are
 * still reporting — what stopped was the data being any good.
 *
 * @param {object} meter - Meter as returned by the meters API.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{label: string, age: string|null}|null} How to word the problem
 *   and how long it has held, or null when the readings are fine.
 */
export const describeReadingProblem = (meter, now = () => new Date()) => {
  const label = UNUSABLE_READING_STATES[meter?.readingState];
  if (!label) return null;

  return { label, age: humaniseAge(meter?.lastValidAt, now) };
};

/**
 * The KPI filters, parent first.
 *
 * Each narrows the ones after it and none of the ones before: picking a
 * category limits which sub-categories exist, but picking a KPI does not
 * rewrite the category above it. One-directional narrowing keeps the controls
 * still under the reader's hand — a bar where every choice rearranges every
 * other control is hard to steer.
 */
const KPI_CHAIN = ['module', 'category', 'subCategory', 'kpi'];

/** Whether a meter survives the meter-id filter. */
const inMeterIds = (meter, filters) =>
  !filters.meterIds?.length || filters.meterIds.includes(meter.meterId);

/** Whether a meter survives the location filter. */
const inSources = (meter, filters) =>
  !filters.sourceIds?.length || filters.sourceIds.includes(meter.sourceId);

/** Whether a KPI matches the chain filters down to a given depth. */
const matchesChain = (kpi, filters, depth) =>
  KPI_CHAIN.slice(0, depth).every((key) => !filters[key] || kpi?.[key] === filters[key]);

/** Sorted distinct values, empties dropped. */
const distinct = (rows, read) =>
  Array.from(new Set(rows.map(read).filter(Boolean))).sort();

/**
 * What each filter can still offer, given what is already selected.
 *
 * Every dropdown was built from the whole fleet, so a reader could pick a
 * category and a sub-category belonging to different KPIs and be shown an
 * empty list with nothing to say which of the two choices was the wrong one.
 *
 * A filter never narrows its own options. Meter ID and Location take several
 * values, and a list that collapsed to what was already chosen could never
 * have a second value added to it.
 *
 * @param {Array<object>} meters - Meters as returned by the meters API.
 * @param {object} filters - Current filter values.
 * @param {string} financialYear - Year whose KPI mapping applies.
 * @returns {object} The values each control can still offer.
 */
export const filterOptionsFor = (meters = [], filters = {}, financialYear) => {
  const rows = (Array.isArray(meters) ? meters : []).map((meter) => ({
    meter,
    kpi: findKpiForYear(meter, financialYear),
  }));

  const named = rows.filter(({ meter }) => inMeterIds(meter, filters));
  const atDepth = (depth) => named.filter(({ kpi }) => matchesChain(kpi, filters, depth));

  return {
    modules: distinct(atDepth(0), ({ kpi }) => kpi?.module),
    categories: distinct(atDepth(1), ({ kpi }) => kpi?.category),
    subCategories: distinct(atDepth(2), ({ kpi }) => kpi?.subCategory),
    kpis: distinct(atDepth(3), ({ kpi }) => kpi?.kpi),

    /* Every other filter decides which meters are worth offering, so a reader
       cannot name one the rest of the bar has already excluded. */
    meterIds: rows
      .filter(
        ({ meter, kpi }) =>
          inSources(meter, filters) && matchesChain(kpi, filters, KPI_CHAIN.length)
      )
      .map(({ meter }) => meter.meterId),

    sourceIds: distinct(named, ({ meter }) => meter.sourceId),
  };
};

/** Which option list answers for each chain filter, and how to name it. */
const CHAIN_FIELDS = {
  module: { options: 'modules', label: 'Module' },
  category: { options: 'categories', label: 'Category' },
  subCategory: { options: 'subCategories', label: 'Sub Category' },
  kpi: { options: 'kpis', label: 'KPI' },
};

/**
 * Drops selections the latest change has made unreachable.
 *
 * Changing a category strands the sub-category beneath it. Leaving that
 * selection in place shows an empty list and blames the data; clearing it
 * silently loses a choice the reader made. It is dropped and named, so the bar
 * reports what it did rather than either.
 *
 * The chain is walked parent first, because clearing one level changes what
 * the next can offer.
 *
 * @param {object} filters - Filter values after the reader's change.
 * @param {Array<object>} meters - Meters as returned by the meters API.
 * @param {string} financialYear - Year whose KPI mapping applies.
 * @returns {{filters: object, dropped: Array<string>}} The surviving filters
 *   and the names of what was cleared.
 */
export const pruneFilters = (filters = {}, meters = [], financialYear) => {
  const next = { ...filters };
  const dropped = [];

  KPI_CHAIN.forEach((key) => {
    if (!next[key]) return;

    const { options, label } = CHAIN_FIELDS[key];
    if (!filterOptionsFor(meters, next, financialYear)[options].includes(next[key])) {
      next[key] = undefined;
      dropped.push(label);
    }
  });

  if (next.meterIds?.length) {
    const offered = filterOptionsFor(meters, { ...next, meterIds: [] }, financialYear).meterIds;
    const kept = next.meterIds.filter((id) => offered.includes(id));

    if (kept.length !== next.meterIds.length) {
      next.meterIds = kept.length ? kept : undefined;
      dropped.push('Meter ID');
    }
  }

  return { filters: next, dropped };
};

/**
 * The financial years worth offering, given what the meters are mapped to.
 *
 * Every year the company reports on was offered, including ones no meter has
 * a KPI mapping for — and a year with no mapping can only ever draw an empty
 * dashboard. The reader had no way to tell that apart from a plant that used
 * no water.
 *
 * When no year matches, every year is returned rather than none: an empty
 * control would leave the page with nothing to scope by at all, which is worse
 * than offering a year that turns out to be empty.
 *
 * @param {Array<object>} meters - Meters as returned by the meters API.
 * @param {Array<{value: string}>} options - Years the company reports on.
 * @returns {Array<object>} The years to offer.
 */
export const financialYearsWithData = (meters = [], options = []) => {
  const mapped = new Set();

  (Array.isArray(meters) ? meters : []).forEach((meter) => {
    (meter?.financialYearKpis || []).forEach((mapping) => {
      if (mapping?.financialYear) mapped.add(mapping.financialYear);
    });
  });

  const list = Array.isArray(options) ? options : [];
  const withData = list.filter((option) => mapped.has(option.value));

  return withData.length ? withData : list;
};

/**
 * The sub-locations worth offering, given which locations are selected.
 *
 * Location takes several values, so this reads a set rather than one id, and
 * an unset filter means every location rather than none. Two locations could
 * name the same sub-location id, so the result is deduplicated — a repeated
 * option in a dropdown is a bug the reader has to notice.
 *
 * A site with no sub-locations returns nothing, and the caller drops the
 * control rather than showing one that can only ever say "All".
 *
 * @param {Array<object>} sources - Locations, each carrying its sub-locations.
 * @param {Array<number>} sourceIds - Locations currently selected, if any.
 * @returns {Array<{value: number, label: string}>} Sub-locations to offer.
 */
export const subLocationsFor = (sources = [], sourceIds = []) => {
  const list = Array.isArray(sources) ? sources : [];
  const chosen = Array.isArray(sourceIds) ? sourceIds : [];

  const within = chosen.length
    ? list.filter((source) => chosen.includes(source.value))
    : list;

  const byValue = new Map();
  within.forEach((source) => {
    (source?.subLocations || []).forEach((subLocation) => {
      if (subLocation?.value == null || byValue.has(subLocation.value)) return;
      byValue.set(subLocation.value, subLocation);
    });
  });

  return Array.from(byValue.values());
};

/**
 * Applies the meters-list filters to a set of meters.
 * KPI-based filters resolve against the meter's mapping for the given financial year.
 * @param {Array<object>} meters - Meters to filter.
 * @param {object} filters - Filter values as decoded from the URL.
 * @param {string} financialYear - Financial year the KPI filters apply to.
 * @returns {Array<object>} Meters matching every supplied filter.
 */
export const filterMeters = (meters, filters = {}, financialYear) =>
  (meters || []).filter((meter) => {
    if (filters.health && !inHealth(meter, filters.health)) return false;
    if (filters.meterIds?.length && !filters.meterIds.includes(meter.meterId)) {
      return false;
    }
    if (filters.sourceIds?.length && !filters.sourceIds.includes(meter.sourceId)) {
      return false;
    }
    if (
      filters.subLocationId !== undefined &&
      meter.subLocationId !== filters.subLocationId
    ) {
      return false;
    }

    const kpi = findKpiForYear(meter, financialYear);
    if (filters.module && kpi?.module !== filters.module) return false;
    if (filters.category && kpi?.category !== filters.category) return false;
    if (filters.subCategory && kpi?.subCategory !== filters.subCategory) return false;
    if (filters.kpi && kpi?.kpi !== filters.kpi) return false;

    return true;
  });
